import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';

export default function IndustriesPage() {
  useLanguageFromPath();
  const [active, setActive] = useState<number | null>(null);
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const industries = [
    {
      icon: 'ri-shopping-cart-2-line',
      name: t('ind_ecommerce_name'),
      desc: t('ind_ecommerce_desc'),
      funding: '$10K–$150K',
      common: [t('ind_ecommerce_c1'), t('ind_ecommerce_c2'), t('ind_ecommerce_c3'), t('ind_ecommerce_c4')],
    },
    {
      icon: 'ri-briefcase-4-line',
      name: t('ind_services_name'),
      desc: t('ind_services_desc'),
      funding: '$15K–$100K',
      common: [t('ind_services_c1'), t('ind_services_c2'), t('ind_services_c3'), t('ind_services_c4')],
    },
    {
      icon: 'ri-restaurant-2-line',
      name: t('ind_restaurants_name'),
      desc: t('ind_restaurants_desc'),
      funding: '$20K–$120K',
      common: [t('ind_restaurants_c1'), t('ind_restaurants_c2'), t('ind_restaurants_c3'), t('ind_restaurants_c4')],
    },
    {
      icon: 'ri-building-2-line',
      name: t('ind_construction_name'),
      desc: t('ind_construction_desc'),
      funding: '$25K–$200K',
      common: [t('ind_construction_c1'), t('ind_construction_c2'), t('ind_construction_c3'), t('ind_construction_c4')],
    },
    {
      icon: 'ri-heart-pulse-line',
      name: t('ind_healthcare_name'),
      desc: t('ind_healthcare_desc'),
      funding: '$20K–$150K',
      common: [t('ind_healthcare_c1'), t('ind_healthcare_c2'), t('ind_healthcare_c3'), t('ind_healthcare_c4')],
    },
    {
      icon: 'ri-home-office-line',
      name: t('ind_realestate_name'),
      desc: t('ind_realestate_desc'),
      funding: '$15K–$100K',
      common: [t('ind_realestate_c1'), t('ind_realestate_c2'), t('ind_realestate_c3'), t('ind_realestate_c4')],
    },
    {
      icon: 'ri-computer-line',
      name: t('ind_technology_name'),
      desc: t('ind_technology_desc'),
      funding: '$10K–$150K',
      common: [t('ind_technology_c1'), t('ind_technology_c2'), t('ind_technology_c3'), t('ind_technology_c4')],
    },
    {
      icon: 'ri-store-line',
      name: t('ind_retail_name'),
      desc: t('ind_retail_desc'),
      funding: '$10K–$100K',
      common: [t('ind_retail_c1'), t('ind_retail_c2'), t('ind_retail_c3'), t('ind_retail_c4')],
    },
    {
      icon: 'ri-truck-line',
      name: t('ind_transport_name'),
      desc: t('ind_transport_desc'),
      funding: '$20K–$200K',
      common: [t('ind_transport_c1'), t('ind_transport_c2'), t('ind_transport_c3'), t('ind_transport_c4')],
    },
    {
      icon: 'ri-scissors-cut-line',
      name: t('ind_beauty_name'),
      desc: t('ind_beauty_desc'),
      funding: '$10K–$75K',
      common: [t('ind_beauty_c1'), t('ind_beauty_c2'), t('ind_beauty_c3'), t('ind_beauty_c4')],
    },
    {
      icon: 'ri-book-open-line',
      name: t('ind_education_name'),
      desc: t('ind_education_desc'),
      funding: '$10K–$80K',
      common: [t('ind_education_c1'), t('ind_education_c2'), t('ind_education_c3'), t('ind_education_c4')],
    },
    {
      icon: 'ri-car-wash-line',
      name: t('ind_auto_name'),
      desc: t('ind_auto_desc'),
      funding: '$15K–$100K',
      common: [t('ind_auto_c1'), t('ind_auto_c2'), t('ind_auto_c3'), t('ind_auto_c4')],
    },
  ];

  const requirements = [
    { icon: 'ri-building-line', text: t('ind_req_1') },
    { icon: 'ri-user-line', text: t('ind_req_2') },
    { icon: 'ri-calendar-line', text: t('ind_req_3') },
    { icon: 'ri-bank-line', text: t('ind_req_4') },
    { icon: 'ri-file-text-line', text: t('ind_req_5') },
  ];

  useEffect(() => {
    document.title = 'Who We Help | Business Funding By Industry | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Kailac helps qualified business owners across 12+ industries access 0% interest funding. E-commerce, construction, healthcare, retail, restaurants, and more. For qualified applicants only.');
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('ind_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('ind_hero_title')}
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mb-4">
            {t('ind_hero_subtitle')}
          </p>
          <p className="text-xs text-gray-400 italic">{t('ind_hero_disclaimer')}</p>
        </div>
      </section>

      {/* Industry Grid */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {industries.map(({ icon, name, desc, funding, common }, idx) => (
              <div
                key={name}
                className={`rounded-2xl border cursor-pointer transition-all ${active === idx ? 'border-[#4F2D63] bg-[#F8F3FB]' : 'border-[#E9D9F2] bg-white hover:border-[#DBB6EE] hover:-translate-y-0.5'}`}
                onClick={() => setActive(active === idx ? null : idx)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#F8F3FB] flex-shrink-0">
                      <i className={`${icon} text-[#4F2D63] text-lg`}></i>
                    </div>
                    <span className="text-xs font-semibold text-[#4F2D63] bg-[#F8F3FB] px-2.5 py-1 rounded-full whitespace-nowrap">
                      {funding}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{name}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">{desc}</p>
                  <div className={`overflow-hidden transition-all duration-300 ${active === idx ? 'max-h-40' : 'max-h-0'}`}>
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-2">{t('ind_common_uses_label')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {common.map((c) => (
                          <span key={c} className="text-xs bg-[#E9D9F2] text-[#4F2D63] px-2.5 py-1 rounded-full">{c}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button className="mt-3 text-xs font-semibold text-[#4F2D63] flex items-center gap-1 cursor-pointer">
                    {active === idx ? t('ind_less_info') : t('ind_common_uses_btn')}
                    <i className={`ri-arrow-down-s-line text-sm transition-transform ${active === idx ? 'rotate-180' : ''}`}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-500 mb-4">{t('ind_not_see')}</p>
            <Link to={lp('/contact', lang)} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap">
              {t('ind_cta_check')} <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* General Requirements */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-3">{t('ind_eligibility_eyebrow')}</p>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('ind_eligibility_title')}</h2>
              <p className="text-base text-gray-500 leading-relaxed mb-6">{t('ind_eligibility_subtitle')}</p>
              {requirements.map(({ icon, text }) => (
                <div key={icon} className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F8F3FB] flex-shrink-0 mt-0.5">
                    <i className={`${icon} text-[#4F2D63] text-sm`}></i>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                </div>
              ))}
              <p className="text-xs text-gray-400 italic mt-4">{t('ind_eligibility_note')}</p>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-[#E9D9F2]">
              <h3 className="text-lg font-bold text-gray-900 mb-5">{t('ind_qualify_card_title')}</h3>
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">{t('ind_qualify_card_desc')}</p>
              <Link to={lp('/contact', lang)} className="block w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap">
                {t('ind_qualify_card_cta')}
              </Link>
              <p className="text-xs text-gray-400 text-center mt-3 italic">{t('ind_qualify_card_note')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* No LLC Banner */}
      <section className="py-14 px-6 lg:px-8 bg-[#2D1547] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#4F2D63]/30 rounded-full translate-x-36 -translate-y-36" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-[#4F2D63]/20 rounded-full -translate-x-28 translate-y-28" />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-2xl bg-[#4F2D63] border border-[#7B4A94]">
                <i className="ri-building-4-line text-[#DBB6EE] text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">{t('ind_no_llc_title')}</h3>
                <p className="text-sm text-[#C4A7D8] leading-relaxed max-w-xl">{t('ind_no_llc_subtitle')}</p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <Link
                to={lp('/start-your-llc', lang)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-[#2D1547] font-semibold text-sm hover:bg-[#F8F3FB] transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('ind_no_llc_cta')} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
